import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Icon, Paper, Tooltip } from '@mui/material';
import { DataGrid, GridActionsColDef, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';

import ProgressBar from '../controls/ProgressBar';
import ErrorMessage from '../controls/ErrorMessage';
import { ListParams } from '../../models/listParams';
import { Portfolio } from '../../models/portfolio/portfolio';

import OverzichtActionMenu from './OverzichtActionMenu';

import { portfolioService } from '../../services/portfolio.service';
import NoActiveOrganisatieMessage from '../controls/NoActiveOrganisatieMessage';
import { MijnOrganisatieListItem } from '../../models/user/mijnOrganisatieListItem';
import { userManagementService } from '../../services/user-management.service';




interface OverzichtPortfoliosProps {
  planId?: string;
  refreshKey?: number;
}

const OverzichtPortfolios: React.FC<OverzichtPortfoliosProps> = ({ 
  planId,
  refreshKey
}) => {
  const navigate = useNavigate();
  const [mijnOrganisatie] = useState<MijnOrganisatieListItem | null>(() =>
    userManagementService.getStoredOrganisatie()
  );
  const [portfolios, setPortfolios] = useState<any[]>([]);
  
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<string>("");
  const [ready, setReady] = useState(false);

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPortfolios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: ListParams = {
        offset: pageIndex * pageSize,
        limit: pageSize,
        sortBy: sortBy && sortDirection ? `${sortBy} ${sortDirection}` : undefined,
      };

      let data = [];
      if (planId) {
        data = await portfolioService.getPortfolioOverviewByPlan(planId, params);
      }
      else {
        data = await portfolioService.getPortfolios(params);
      }

      setPortfolios(data.items);
      setTotalCount(data.count);
    }
    catch (error: any) {
      if (error.message === 'Unable to refresh token') {
        navigate('/verlopen');
      }
      setError(error.message || 'Er is een fout opgetreden');
    }
    finally {
      setLoading(false);
    }
  }, [navigate, planId, pageIndex, pageSize, sortBy, sortDirection]);

  const onClickPortfolioLink = useCallback((portfolioId: string) => {
    navigate(`/dossiers/${portfolioId}`);
  }, [navigate]);

  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    fetchPortfolios();
  }, [ready, fetchPortfolios, refreshKey]);

  const gridColumns = useMemo<(GridColDef<any> | GridActionsColDef<any>)[]>(() => {
    const baseColumns: (GridColDef<any> | GridActionsColDef<any>)[] = [
      {
        field: 'name',
        headerName: 'Naam dossier',
        flex: 3,
        renderCell: (params: any) => (
          <Link className="lnk" to={`/dossiers/${params.row.id}`}>
            {params.row.name}
          </Link>
        ),
      }
    ];

    if (planId) {
      baseColumns.push({
        field: "approvalPending",
        headerName: "Deelname voor dit plan geaccepteerd?",
        renderHeader: () => (
          <Tooltip
            title="Geeft aan of deelname voor dit plan geaccepteerd is in dit afstemmingsdossier"
            arrow
          >
            <span>Deelname voor dit plan geaccepteerd?</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<any>) => (
          <span>{params.row.approvalPending ? "Nee" : "Ja"}</span>
        )
      });
    }

    if (!planId) {
      baseColumns.push({
        field: "approvalPending",
        headerName: "Deelname volledig geaccepteerd?",
        renderHeader: () => (
          <Tooltip
            title="Geeft aan of alle plannen binnen uw organisatie geaccepteerd of afgewezen zijn in dit afstemmingsdossier"
            arrow
          >
            <span>Deelname volledig geaccepteerd?</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<any>) => {
          const isPending = params.row.approvalPending;
          const label = isPending ? "Nee" : "Ja";
          const tooltipText = isPending
            ? "Er is nog niet voor alle plannen van uw organisatie een keuze gemaakt met betrekking tot de deelname in het afstemmingsdossier"
            : "De deelname is voor al uw plannen in het afstemmingsdossier verwerkt";

          return (
            <Tooltip title={tooltipText} arrow>
              <span>{label}</span>
            </Tooltip>
          );
        }
      });

      baseColumns.push({ 
        field: 'inclusionRequestCount', 
        headerName: 'Aantal plannen in afwachting',
        renderHeader: () => (
          <Tooltip
            title="Het totaal aantal plannen in het afstemmingsdossier die nog wachten op goedkeuring om deel te nemen"
            arrow
          >
            <span>Aantal plannen in afwachting</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false
      });
      baseColumns.push({ 
        field: 'planCount', 
        headerName: 'Aantal plannen actief',
        renderHeader: () => (
          <Tooltip
            title="Het totaal aantal plannen die actief deelnemen in het afstemmingsdossier"
            arrow
          >
            <span>Aantal plannen actief</span>
          </Tooltip>
        ), 
        flex: 2,
        sortable: false,
        filterable: false 
      });

      baseColumns.push({
        field: 'consortiums',
        headerName: 'Samenwerkingsverbanden',
        renderHeader: () => (
          <Tooltip
            title="Aantal gekoppelde samenwerkingsverbanden binnen dit afstemmingsdossier"
            arrow
          >
            <span>Samenwerkingsverbanden</span>
          </Tooltip>
        ),
        flex: 2,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<any>) => {
          const consortiums = Array.isArray(params.row.consortiums) ? params.row.consortiums : [];
          const consortiumNames = consortiums
            .map((item: any) => item?.name)
            .filter(Boolean)
            .sort((a: string, b: string) => a.localeCompare(b, 'nl-NL'))
            .join(', ');
          const tooltipText = consortiumNames ? `Samenwerkingsverbanden: ${consortiumNames}` : 'Geen samenwerkingsverbanden gekoppeld';

          return (
            <Tooltip title={tooltipText} arrow>
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <Badge
                  badgeContent={consortiums.length}
                  color={consortiums.length === 0 ? 'secondary' : 'primary'}
                  showZero
                  sx={{ mr: 1 }}
                >
                  <Icon fontSize="small">groups</Icon>
                </Badge>
              </span>
            </Tooltip>
          );
        }
      });
    }

    // baseColumns.push({
    //   field: 'isActiveDossier',
    //   headerName: 'Dossier is actief',
    //   flex: 2,
    //   sortable: false,
    //   filterable: false,
    //   renderCell: (params: GridRenderCellParams<any>) => (
    //     <span>{params.row.planCount > 1 ? 'Ja' : 'Nee'}</span>
    //   )
    // });

    baseColumns.push({
      field: 'actions',
      headerName: '',
      width: 70,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<Portfolio>) => (
        <OverzichtActionMenu params={params} onPortfolioUpdate={fetchPortfolios} />
      )
    });
    
    return baseColumns;
  }, [planId, fetchPortfolios, onClickPortfolioLink]);

  const onChangePagination = ({ page, pageSize }: { page: number, pageSize: number }) => {
    setPageIndex(page);
    setPageSize(pageSize);
  };

  const handleSortChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      setSortBy(field);
      setSortDirection(sort || "asc");
    }
    else {
      setSortBy("");
      setSortDirection("");
    }
  };

  if (!mijnOrganisatie) {
    return (
      <>
        <Paper>
          <NoActiveOrganisatieMessage />
        </Paper>
      </>
    )
  }

  return (
    <>
      {(!planId || portfolios.length > 0) && (
        <div className="grid-responsive">
          <Paper sx={{ width: '100%' }}>
            <DataGrid
              rows={portfolios}
              columns={gridColumns}
              getRowId={(row) => row.id || ''}
              disableColumnFilter
              disableRowSelectionOnClick
              rowCount={totalCount}
              paginationMode="server"
              pageSizeOptions={[5, 10, 25]}
              initialState={{
                pagination: {
                  paginationModel: {
                    page: pageIndex,
                    pageSize: pageSize
                  }
                }
              }}
              onPaginationModelChange={onChangePagination}
              onSortModelChange={handleSortChange} // Custom sort handling
              sortingMode="server" 
              sx={{ border: 0 }}
            />
          </Paper>
        </div>
      )}

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

    </>
  );
};

export default OverzichtPortfolios;
